/* San Rocco Italia | Variant gallery prototype v0.1
 * Shopify Studio 16.1.0: image-order grouping with shared images.
 * Test in a duplicate theme only. Keep the existing app until all gallery
 * behaviours (including mobile and the zoom modal) have been validated.
 */
(() => {
  'use strict';

  if (window.SRIVariantGallery) return;

  const HIDDEN_CLASS = 'sri-variant-media-hidden';
  const metadataCache = new WeakMap();

  const mediaIdFromTarget = (value) => String(value || '').split('-').pop();

  function readMetadata(gallery) {
    if (metadataCache.has(gallery)) return metadataCache.get(gallery);

    const script = gallery.querySelector('script[data-sri-variant-gallery]');
    if (!script) return null;

    try {
      const data = JSON.parse(script.textContent);
      if (!Array.isArray(data.media) || !Array.isArray(data.variants)) return null;

      const mediaIds = data.media.map(String);
      const variantMedia = new Map();
      const anchors = new Set();

      for (const variant of data.variants) {
        const assigned = variant.mediaId == null ? null : String(variant.mediaId);
        variantMedia.set(String(variant.id), assigned);
        if (assigned && mediaIds.includes(assigned)) anchors.add(assigned);
      }

      // The ORIGINAL product.media order, not the current gallery DOM order,
      // determines each group's boundaries. Initial items before the first
      // assigned image belong to every variant.
      const groupByMedia = new Map();
      let group = null;
      for (const id of mediaIds) {
        if (anchors.has(id)) group = id;
        groupByMedia.set(id, group);
      }

      const metadata = {
        variantMedia,
        groupByMedia,
        initialVariantId: String(data.initialVariantId || ''),
        hasAnchors: anchors.size > 0,
      };
      metadataCache.set(gallery, metadata);
      return metadata;
    } catch (error) {
      console.warn('[SRI variant gallery] Could not read product gallery data.', error);
      return null;
    }
  }

  function toggleMedia(item, visible) {
    item.classList.toggle(HIDDEN_CLASS, !visible);
    item.hidden = !visible;
    if (visible) {
      item.removeAttribute('aria-hidden');
    } else {
      item.setAttribute('aria-hidden', 'true');
    }
  }

  function refreshSlider(slider) {
    if (!slider) return;
    if (typeof slider.resetPages === 'function') {
      slider.resetPages();
    } else if (typeof slider.initPages === 'function') {
      slider.initPages();
    }
  }

  function apply(gallery, variantId) {
    if (!gallery) return;
    const metadata = readMetadata(gallery);
    if (!metadata) return;

    const selectedMediaId = metadata.variantMedia.get(String(variantId));
    // If a product/variant has no assigned media, don't hide anything.
    const shouldFilter = metadata.hasAnchors && Boolean(selectedMediaId);
    const isVisible = (rawId) => {
      if (!shouldFilter) return true;
      const id = mediaIdFromTarget(rawId);
      const group = metadata.groupByMedia.get(id);
      return group === undefined || group === null || group === selectedMediaId;
    };

    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails-"]');
    if (!viewer) return;

    const slides = Array.from(viewer.querySelectorAll('li[data-media-id]'));
    const thumbItems = Array.from(thumbnails?.querySelectorAll('li[data-target]') || []);

    slides.forEach((item) => toggleMedia(item, isVisible(item.dataset.mediaId)));
    thumbItems.forEach((item) => toggleMedia(item, isVisible(item.dataset.target)));


    // Selected colour first, shared images afterwards.
    if (shouldFilter) {
      const originalOrder = new Map(
        Array.from(metadata.groupByMedia.keys(), (id, index) => [id, index])
      );

      const sortByGalleryOrder = (a, b) => {
        const aId = mediaIdFromTarget(a.dataset.mediaId || a.dataset.target);
        const bId = mediaIdFromTarget(b.dataset.mediaId || b.dataset.target);
        const priority = (id) => {
          const group = metadata.groupByMedia.get(id);
          return group === selectedMediaId ? 0 : group == null ? 1 : 2;
        };
        return priority(aId) - priority(bId)
          || (originalOrder.get(aId) ?? Infinity) - (originalOrder.get(bId) ?? Infinity);
      };

      const reorder = (items) => {
        if (!items.length) return;
        const list = items[0].parentElement;
        items.sort(sortByGalleryOrder).forEach((item) => list.appendChild(item));
      };

      reorder(slides);
      reorder(thumbItems);
    }


    const visibleSlides = slides.filter((item) => !item.hidden);
    gallery.classList.toggle('sri-variant-gallery-single', visibleSlides.length <= 1);
    gallery.dataset.sriVisibleMediaCount = String(visibleSlides.length);

    // The selected variant's lead image should be visible and active. On
    // normal variant changes, Studio has already activated it for us.
    const activeSlide = slides.find((item) => item.classList.contains('is-active'));
    if (activeSlide?.hidden && visibleSlides.length) {
      const preferred = visibleSlides.find(
        (item) => mediaIdFromTarget(item.dataset.mediaId) === selectedMediaId
      ) || visibleSlides[0];
      gallery.setActiveMedia?.(preferred.dataset.mediaId, false);
    }

    // Native slider-components calculate pages from rendered items; refresh
    // after hidden items have been removed from layout.
    requestAnimationFrame(() => {
      refreshSlider(viewer);
      refreshSlider(thumbnails);
    });
  }

  function initialise(root = document) {
    const galleries = root.matches?.('media-gallery')
      ? [root]
      : root.querySelectorAll?.('media-gallery') || [];

    galleries.forEach((gallery) => {
      const metadata = readMetadata(gallery);
      if (!metadata) return;
      apply(gallery, metadata.initialVariantId);
    });
  }

  function start() {
    initialise();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
        if (!data?.variant) return;
        const gallery = document.getElementById(`MediaGallery-${data.sectionId}`);
        apply(gallery, data.variant.id);
      });
    } else {
      console.warn('[SRI variant gallery] Shopify variant event listener unavailable.');
    }
  }

  // New product-info elements may appear after product swaps or in the editor.
  document.addEventListener('product-info:loaded', (event) => {
    requestAnimationFrame(() => initialise(event.target));
  });
  document.addEventListener('shopify:section:load', (event) => {
    requestAnimationFrame(() => initialise(event.target));
  });

  window.SRIVariantGallery = { initialise, apply };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
